import { FC, memo } from "react";
import { observer } from "mobx-react";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";

import { useAppStore } from "../../../../hooks/useAppStore";
import { useChatStore } from "../../../../hooks/useChatStore";
import { MODEL_OPTIONS } from "../../../../config/chatModels";

/**
 * Lets the user pick which LLM answers in this chat. Gated on the
 * CHAT_ENABLE_MODEL_SELECTION config flag. The choice lives on
 * ChatStore.selectedModel and is sent as `model_version` when a chat is created;
 * on an existing chat, changing it updates the chat over REST (see
 * ChatStore.changeSelectedModel). Disabled mid-turn, while loading, and on
 * closed / view-only chats (ChatStore.canChangeModel).
 */
const ModelSelector: FC = observer(() => {
  const appStore = useAppStore();
  const chatStore = useChatStore();

  if (!appStore?.isModelSelectionEnabled) return null;
  // A mocked chat calls no real model, so there is nothing to choose.
  if (chatStore.isMockEnabled) return null;

  return (
    <Select
      size="small"
      value={chatStore.selectedModel}
      disabled={!chatStore.canChangeModel}
      onChange={(e: SelectChangeEvent<string>) => {
        void chatStore.changeSelectedModel(e.target.value);
      }}
      inputProps={{ "aria-label": "Select model" }}
      sx={{
        height: 32,
        fontFamily: "Manrope, sans-serif",
        fontSize: 13,
        fontWeight: 600,
        color: "text.primary",
        borderRadius: "16px",
        // MUI's default min-height/line-height/padding on the inner div push
        // the label to the top of a 32px box; center it with flex instead.
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          height: "100%",
          minHeight: 0,
          boxSizing: "border-box",
          lineHeight: 1,
          py: 0,
          pl: 1.5,
          pr: "28px !important",
        },
        "& .MuiSelect-icon": {
          right: 6,
          fontSize: 18,
          color: "text.secondary",
        },
        "& .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "primary.main",
        },
      }}
    >
      {MODEL_OPTIONS.map((option) => (
        <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13 }}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
});

export default memo(ModelSelector);
